import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';

function ChatBotModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I’m the Dwinsoft Technologies assistant. How can I help you today?',
    },
  ]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [typingDots, setTypingDots] = useState('Typing');

  const messagesEndRef = useRef(null);

  // Animate the "Typing..." message
  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setTypingDots((prev) =>
          prev === 'Typing...' ? 'Typing' : prev + '.'
        );
      }, 500);
      return () => clearInterval(interval);
    }
  }, [loading]);

  // Auto scroll to bottom on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!userInput.trim()) return;

    const updatedMessages = [...messages, { role: 'user', content: userInput }];
    setMessages(updatedMessages);
    setUserInput('');
    setLoading(true);

    // Add "Typing..." message temporarily
    setMessages([...updatedMessages, { role: 'assistant', content: 'Typing...' }]);

    try {
      const url = process.env.REACT_APP_PUBLIC_URL;
      const response = await axios.post(`${url}/api/bot/chat`, {
        message: updatedMessages,
      });

      const newMessages = [
        ...updatedMessages,
        { role: 'assistant', content: response.data.reply },
      ];
      setMessages(newMessages);
    } catch (error) {
      // Handle error when chatbot API request fails
      const errorMessages = [
        ...updatedMessages,
        { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
      ];
      setMessages(errorMessages);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  if (window.location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <div>
      <button className="chatbot-button axil-btn btn-fill-primary btn-large" onClick={() => setIsOpen(true)}>
        Chat
      </button>

      {isOpen && (
        <div className="chatbot-modal-backdrop">
          <div className="chatbot-modal">
            <div className="chatbot-header">
              Dwinsoft Assistant
              <button className="chatbot-close" onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </button>
            </div>

            <div className="chatbot-messages">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`chatbot-message-wrapper ${msg.role === 'user' ? 'user' : 'assistant'}`}
                >
                  <div className={`chatbot-message ${msg.content === 'Typing...' ? 'typing' : ''}`}>
                    {msg.content === 'Typing...' && loading ? typingDots : msg.content}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="chatbot-input-area">
              <input
                type="text"
                placeholder="Ask something about Dwinsoft..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              <button onClick={handleSend} disabled={loading}>
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatBotModal;
